/**
 * API Client - Axios 인스턴스 설정
 *
 * 기존 axios 인터셉터와 통합하여 사용
 * 도메인별 API 모듈에서 이 클라이언트를 사용
 *
 * @example
 * import { apiClient } from '@new/api/client'
 *
 * const response = await apiClient.get('/users')
 */
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import store from '@/store';

// 기존 httpInterceptor가 이미 설정되어 있으므로
// 동일한 axios 인스턴스를 사용
export const apiClient: AxiosInstance = axios;

/**
 * 인증 토큰 없이 요청하기 위한 클라이언트
 */
export const publicClient: AxiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * 콘솔 API 전용 클라이언트
 */
export const consoleClient: AxiosInstance = axios.create({
  baseURL: process.env.VUE_APP_CONSOLE_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * 유효한 토큰 가져오기
 */
export const getValidToken = (): Promise<string | null> => store.dispatch('getValidToken');

/**
 * 안전한 API 응답 타입
 */
export interface SafeRequestResult<T> {
  data: T | null;
  error: Error | null;
}

/**
 * API 응답 래퍼 - 일관된 에러 처리
 * @param request - axios 요청 프로미스
 * @returns 성공 시 data, 실패 시 error를 포함한 객체
 */
export async function safeRequest<T>(request: Promise<AxiosResponse<T>>): Promise<SafeRequestResult<T>> {
  try {
    const response = await request;
    return { data: response.data, error: null };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

export default apiClient;
